import { createTheme, lighten, darken, getContrastRatio } from '@mui/material/styles';
import type {} from '@mui/x-data-grid/themeAugmentation';
import { TYPE } from './typography';
import { getPaletteByKey } from '@/config/colorPalettes';

export { TYPE } from './typography';

/** A company brand colour is only applied when it's a valid 6-digit hex. The
 * company colour-picker (input[type=color]) always emits #rrggbb, so anything
 * else is treated as "no brand override" and we fall back to the palette. */
const HEX6 = /^#[0-9a-fA-F]{6}$/;

/**
 * Palette-derived chart colors — 6 visually distinct colors for charts.
 * Lightens each color for dark mode to maintain contrast on dark backgrounds.
 */
export function getChartColors(paletteKey?: string | null, mode: 'light' | 'dark' = 'light') {
  const p = getPaletteByKey(paletteKey);
  const isLight = mode === 'light';
  return [
    isLight ? p.primary : p.primaryLight,
    isLight ? p.secondary : p.secondary,
    isLight ? p.success : p.success,
    isLight ? p.warning : p.warning,
    isLight ? p.info : p.info,
    isLight ? p.error : p.error,
  ];
}

/**
 * Card style dimension for the "AnooreHR Panel" design system.
 *   balanced — current product look (subtle hover shadow, 12px radius). DEFAULT,
 *              so existing pages are unchanged unless a style is explicitly chosen.
 *   minimal  — flat: no shadow, 10px radius, hover = background tint only.
 *   soft     — friendlier: 20px radius, soft resting shadow.
 */
export type PanelStyle = 'balanced' | 'minimal' | 'soft';

interface CardStyleTokens {
  radius: number;
  shadow: string;
  hoverShadow: string;
}

function cardStyleFor(style: PanelStyle, isLight: boolean): CardStyleTokens {
  switch (style) {
    case 'minimal':
      return { radius: 10, shadow: 'none', hoverShadow: 'none' };
    case 'soft':
      return {
        radius: 20,
        shadow: isLight ? '0 6px 20px rgba(16,24,40,0.08)' : '0 6px 20px rgba(0,0,0,0.4)',
        hoverShadow: isLight ? '0 10px 28px rgba(16,24,40,0.12)' : '0 10px 28px rgba(0,0,0,0.5)',
      };
    case 'balanced':
    default:
      return { radius: 12, shadow: 'none', hoverShadow: '0 4px 12px rgba(0,0,0,0.05)' };
  }
}

export function getTheme(
  mode: 'light' | 'dark' = 'light',
  paletteKey?: string | null,
  brandColor?: string | null,
  style: PanelStyle = 'balanced',
) {
  const isLight = mode === 'light';
  const card = cardStyleFor(style, isLight);
  let colorPalette = getPaletteByKey(paletteKey);

  // Company brand colour → primary. We derive light/dark variants and pick a
  // contrast-safe text colour (white vs near-black) so a light brand colour
  // (e.g. yellow) doesn't produce unreadable white-on-yellow buttons. The
  // override flows through every colorPalette.primary reference below, so the
  // accent (focus rings, switches, toggles, buttons) stays internally consistent.
  let primaryContrast = '#FFFFFF';
  if (brandColor && HEX6.test(brandColor)) {
    primaryContrast = getContrastRatio(brandColor, '#FFFFFF') >= 3 ? '#FFFFFF' : '#1A1A2E';
    colorPalette = {
      ...colorPalette,
      primary: brandColor,
      primaryLight: lighten(brandColor, 0.25),
      primaryDark: darken(brandColor, 0.2),
    };
  }

  return createTheme({
    palette: {
      mode,
      primary: {
        main: colorPalette.primary,
        light: colorPalette.primaryLight,
        dark: colorPalette.primaryDark,
        contrastText: primaryContrast,
      },
      secondary: {
        main: colorPalette.secondary,
        contrastText: '#FFFFFF',
      },
      success: { main: colorPalette.success },
      error: { main: colorPalette.error },
      warning: { main: colorPalette.warning },
      info: { main: colorPalette.info },
      background: {
        default: isLight ? colorPalette.background : '#0F1117',
        paper: isLight ? colorPalette.paper : '#1A1C25',
      },
      text: {
        primary: isLight ? '#1A1A2E' : '#E4E6EB',
        secondary: isLight ? '#6B7280' : '#9CA3AF',
      },
      divider: isLight ? '#E8EAF0' : '#2D303A',
      action: {
        selected: isLight ? `rgba(${parseInt(colorPalette.primary.slice(1,3), 16)},${parseInt(colorPalette.primary.slice(3,5), 16)},${parseInt(colorPalette.primary.slice(5,7), 16)},0.06)` : `rgba(${parseInt(colorPalette.primary.slice(1,3), 16)},${parseInt(colorPalette.primary.slice(3,5), 16)},${parseInt(colorPalette.primary.slice(5,7), 16)},0.12)`,
        hover: isLight ? '#F9FAFB' : '#22242D',
      },
    },
    typography: {
      fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
      fontSize: 14.7,
      h1: { fontSize: TYPE['5xl'], fontWeight: 700, letterSpacing: '-0.02em' },
      h2: { fontSize: TYPE['4xl'], fontWeight: 700 },
      h3: { fontSize: TYPE['3xl'], fontWeight: 700 },
      h4: { fontSize: TYPE['2xl'], fontWeight: 600 },
      h5: { fontSize: TYPE.xl, fontWeight: 600 },
      h6: { fontSize: TYPE.lg, fontWeight: 600 },
      subtitle1: { fontSize: TYPE.lg, fontWeight: 600, lineHeight: 1.5 },
      subtitle2: { fontSize: TYPE.md, fontWeight: 600, lineHeight: 1.5 },
      body1: { fontSize: TYPE.lg, lineHeight: 1.5 },
      body2: { fontSize: TYPE.md, lineHeight: 1.5 },
      caption: { fontSize: TYPE.base },
      overline: {
        fontSize: TYPE.sm,
        fontWeight: 600,
        letterSpacing: '0.06em',
        textTransform: 'uppercase' as const,
      },
      button: {
        fontSize: TYPE.md,
        fontWeight: 600,
        textTransform: 'none' as const,
      },
    },
    shape: { borderRadius: 8 },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          html: {
            fontSize: 14.7,
            fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
          },
          body: {
            fontSize: '0.9375rem',
            fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
            backgroundColor: isLight ? '#F7F8FB' : '#0F1117',
            WebkitFontSmoothing: 'antialiased',
            MozOsxFontSmoothing: 'grayscale',
          },
          // Prevent any element from overflowing the viewport width
          '*, *::before, *::after': {
            minWidth: 0,
          },
          // Accessibility — keyboard focus ring. MUI's default focus styling
          // varies by component; this unifies the look via :focus-visible so
          // mouse clicks don't show a ring but Tab navigation always does.
          '*:focus-visible': {
            outline: `2px solid ${colorPalette.primary}`,
            outlineOffset: 2,
            borderRadius: 4,
          },
          // Skip-to-main link — hidden until focused, then becomes a
          // high-contrast pill in the top-left corner. Screen-reader users
          // and keyboard-first users Tab to it before anything else.
          '.skip-to-main': {
            position: 'absolute',
            top: -9999,
            left: -9999,
            zIndex: 9999,
            padding: '8px 16px',
            background: colorPalette.primary,
            color: primaryContrast,
            fontWeight: 600,
            fontSize: '0.875rem',
            borderRadius: 8,
            textDecoration: 'none',
            '&:focus, &:focus-visible': {
              top: 12,
              left: 12,
              outline: `2px solid ${isLight ? '#FFFFFF' : colorPalette.background}`,
              outlineOffset: 2,
            },
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: card.radius,
            boxShadow: card.shadow,
            border: `1px solid ${isLight ? '#E8EAF0' : '#2D303A'}`,
            transition: 'border-color 0.15s, box-shadow 0.15s, background 0.25s',
            maxWidth: '100%',
            overflow: 'hidden',
            '&:hover': {
              borderColor: isLight ? '#C7CBD4' : '#3D414B',
              boxShadow: card.hoverShadow,
              ...(style === 'minimal' && {
                backgroundColor: isLight ? '#FCFCFD' : '#1E212B',
              }),
            },
          },
        },
      },
      MuiCardContent: {
        styleOverrides: {
          root: {
            overflow: 'visible',
          },
        },
      },
      MuiTable: {
        defaultProps: {
          size: 'small',
        },
      },
      MuiTableContainer: {
        styleOverrides: {
          root: {
            overflowX: 'auto' as const,
          },
        },
      },
      MuiButton: {
        defaultProps: {
          size: 'small',
        },
        styleOverrides: {
          root: {
            textTransform: 'none' as const,
            fontWeight: TYPE.semibold,
            borderRadius: 8,
          },
          sizeSmall: {
            fontSize: TYPE.base,
            padding: '6px 14px',
          },
          sizeMedium: {
            fontSize: TYPE.md,
            padding: '8px 16px',
          },
          sizeLarge: {
            fontSize: TYPE.lg,
            padding: '10px 22px',
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: {
            fontSize: TYPE.xs,
            fontWeight: TYPE.semibold,
            borderRadius: 6,
            height: 'auto',
          },
          sizeSmall: {
            fontSize: TYPE.xs,
            padding: '3px 10px',
            height: 'auto',
          },
          label: {
            padding: '0 4px',
          },
          labelSmall: {
            padding: '0 2px',
          },
        },
      },
      MuiTextField: {
        defaultProps: {
          size: 'small',
          variant: 'outlined',
        },
      },
      MuiInputBase: {
        styleOverrides: {
          root: { fontSize: TYPE.md },
          input: { fontSize: TYPE.md, padding: '8.5px 14px' },
        },
      },
      MuiInputLabel: {
        styleOverrides: {
          root: {
            fontSize: TYPE.md,
            fontWeight: TYPE.medium,
          },
          sizeSmall: {
            fontSize: TYPE.base,
          },
        },
      },
      MuiFormLabel: {
        styleOverrides: {
          root: {
            fontSize: TYPE.md,
            fontWeight: TYPE.medium,
          },
        },
      },
      MuiFormHelperText: {
        styleOverrides: {
          root: {
            fontSize: TYPE.xs,
            lineHeight: 1.4,
            marginTop: 4,
          },
        },
      },
      MuiFormControlLabel: {
        styleOverrides: {
          label: {
            fontSize: TYPE.md,
            fontWeight: TYPE.medium,
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            fontSize: TYPE.md,
            borderRadius: 8,
          },
          input: {
            fontSize: TYPE.md,
            '&::placeholder': {
              fontSize: TYPE.md,
            },
          },
          inputSizeSmall: {
            padding: '8.5px 14px',
          },
        },
      },
      MuiSelect: {
        styleOverrides: {
          select: {
            fontSize: TYPE.md,
          },
        },
      },
      // Enforce the theme font + size on dropdown options so the open menu
      // matches the field (was inheriting body1 / TYPE.lg → looked oversized).
      MuiMenuItem: {
        styleOverrides: {
          root: {
            fontSize: TYPE.md,
            fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
          },
        },
      },
      MuiAutocomplete: {
        styleOverrides: {
          option: {
            fontSize: TYPE.md,
            fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
          },
          input: {
            fontSize: TYPE.md,
          },
        },
      },
      MuiSwitch: {
        styleOverrides: {
          root: {
            width: 42,
            height: 24,
            padding: 0,
            overflow: 'visible',
            // Small variant uses the same dimensions
            '&.MuiSwitch-sizeSmall': {
              width: 36,
              height: 20,
              '& .MuiSwitch-switchBase': {
                padding: 2,
                '&.Mui-checked': {
                  transform: 'translateX(16px)',
                },
              },
              '& .MuiSwitch-thumb': {
                width: 16,
                height: 16,
              },
            },
          },
          switchBase: {
            padding: 2,
            '&.Mui-checked': {
              transform: 'translateX(18px)',
              color: '#fff',
              '& + .MuiSwitch-track': {
                backgroundColor: colorPalette.primary,
                opacity: 1,
                border: 'none',
              },
            },
            '&.Mui-disabled + .MuiSwitch-track': {
              opacity: 0.3,
            },
          },
          thumb: {
            width: 20,
            height: 20,
            boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
          },
          track: {
            borderRadius: 12,
            backgroundColor: isLight ? '#B0BEC5' : '#4A4D57',
            opacity: 1,
            transition: 'background-color 0.2s',
          },
        },
      },
      MuiTableHead: {
        styleOverrides: {
          root: {
            '& .MuiTableCell-head': {
              backgroundColor: isLight ? '#FAFBFC' : '#15171F',
              fontSize: TYPE.xs,
              fontWeight: TYPE.semibold,
              textTransform: 'uppercase' as const,
              letterSpacing: '0.04em',
              color: isLight ? '#6B7280' : '#9CA3AF',
              padding: '8px 12px',
              borderBottom: `1px solid ${isLight ? '#E8EAF0' : '#2D303A'}`,
            },
          },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          root: {
            fontSize: TYPE.base,
            padding: '6px 12px',
            borderBottom: `1px solid ${isLight ? '#F3F4F6' : '#22242D'}`,
          },
        },
      },
      MuiTableRow: {
        styleOverrides: {
          root: {
            transition: 'background 0.1s',
            '&:hover': {
              backgroundColor: isLight ? '#F9FAFB' : '#22242D',
            },
            '&:last-child td': {
              borderBottom: 'none',
            },
          },
        },
      },
      MuiDataGrid: {
        defaultProps: {
          rowHeight: 52,
          columnHeaderHeight: 44,
        },
        styleOverrides: {
          root: {
            border: 'none',
            fontSize: TYPE.base,
            width: '100%',
            maxWidth: '100%',
            '& .MuiDataGrid-columnHeader': {
              backgroundColor: isLight ? '#FAFBFC' : '#15171F',
              fontWeight: TYPE.semibold,
              fontSize: TYPE.xs,
              textTransform: 'uppercase',
              letterSpacing: '0.04em',
              color: isLight ? '#6B7280' : '#9CA3AF',
              padding: '0 12px',
            },
            '& .MuiDataGrid-cell': {
              fontSize: TYPE.base,
              padding: '0 12px',
              borderBottom: `1px solid ${isLight ? '#F3F4F6' : '#22242D'}`,
              display: 'flex',
              alignItems: 'center',
            },
            '& .MuiDataGrid-row': {
              '&:hover': {
                backgroundColor: isLight ? '#F9FAFB' : '#22242D',
              },
            },
            '& .MuiDataGrid-footerContainer': {
              fontSize: TYPE.sm,
              borderTop: `1px solid ${isLight ? '#E8EAF0' : '#2D303A'}`,
            },
          },
        },
      },
      MuiDrawer: {
        styleOverrides: {
          paper: {
            borderRight: 'none',
            backgroundColor: isLight ? '#FFFFFF' : '#1A1C25',
          },
        },
      },
      MuiIconButton: {
        styleOverrides: {
          root: {
            transition: 'background 0.12s',
            '&:hover': {
              backgroundColor: isLight ? '#F3F4F6' : '#2D303A',
            },
          },
        },
      },
      MuiToggleButtonGroup: {
        styleOverrides: {
          root: {
            borderRadius: 8,
            border: `1px solid ${isLight ? '#E8EAF0' : '#2D303A'}`,
            '& .MuiToggleButtonGroup-grouped': {
              border: 'none',
              '&:not(:first-of-type)': {
                borderLeft: `1px solid ${isLight ? '#E8EAF0' : '#2D303A'}`,
                borderRadius: 8,
                marginLeft: 0,
              },
              '&:first-of-type': {
                borderRadius: 8,
              },
            },
          },
        },
      },
      MuiToggleButton: {
        styleOverrides: {
          root: {
            textTransform: 'none' as const,
            fontWeight: TYPE.medium,
            fontSize: TYPE.base,
            padding: '4px 14px',
            color: isLight ? '#6B7280' : '#9CA3AF',
            '&.Mui-selected': {
              fontWeight: TYPE.semibold,
              backgroundColor: isLight
                ? `rgba(${parseInt(colorPalette.primary.slice(1,3), 16)},${parseInt(colorPalette.primary.slice(3,5), 16)},${parseInt(colorPalette.primary.slice(5,7), 16)},0.08)`
                : `rgba(${parseInt(colorPalette.primary.slice(1,3), 16)},${parseInt(colorPalette.primary.slice(3,5), 16)},${parseInt(colorPalette.primary.slice(5,7), 16)},0.16)`,
              color: colorPalette.primary,
              '&:hover': {
                backgroundColor: isLight
                  ? `rgba(${parseInt(colorPalette.primary.slice(1,3), 16)},${parseInt(colorPalette.primary.slice(3,5), 16)},${parseInt(colorPalette.primary.slice(5,7), 16)},0.12)`
                  : `rgba(${parseInt(colorPalette.primary.slice(1,3), 16)},${parseInt(colorPalette.primary.slice(3,5), 16)},${parseInt(colorPalette.primary.slice(5,7), 16)},0.20)`,
              },
            },
          },
          sizeSmall: {
            fontSize: TYPE.sm,
            padding: '3px 10px',
          },
        },
      },
      MuiAlert: {
        styleOverrides: {
          root: {
            borderRadius: 8,
            fontSize: TYPE.md,
          },
        },
      },
      MuiDialog: {
        styleOverrides: {
          // Cap every dialog to the viewport so long forms scroll internally
          // instead of pushing the action bar (Cancel / Save / Create Employee)
          // below the fold. MUI v6 sets its default cap (calc(100% - 64px)) as a
          // `scroll: 'paper'` *variant* inside the styled component; theme
          // styleOverrides for the `paper` slot are emitted AFTER those variants,
          // so a maxHeight here wins. (The `paperScrollPaper` slot is NOT consumed
          // by MUI's overridesResolver — verified — so it must go on `paper`.)
          // 92vh is viewport-relative, immune to the container-height chain that
          // was letting these dialogs overflow.
          paper: {
            borderRadius: 12,
            maxHeight: '92vh',
          },
        },
      },
      MuiDialogActions: {
        styleOverrides: {
          root: {
            // Keep the submit/cancel bar pinned and visible when the content
            // above it scrolls, so the primary action is always reachable.
            position: 'sticky',
            bottom: 0,
            backgroundColor: isLight ? colorPalette.paper : '#1A1C25',
            borderTop: `1px solid ${isLight ? '#E8EAF0' : '#2D303A'}`,
            zIndex: 1,
          },
        },
      },
    },
  });
}

export default getTheme('light');
